/**
 * The notification state the settings screen reads and drives.
 *
 * Enabling is the only part that must happen inside a user gesture, so it is
 * the only part exposed as an action the button calls directly — everything
 * else can run whenever.
 */
import { useCallback, useEffect, useState } from "react";

import type { LibraryShow } from "@/api/schemas";
import type { ShowDetail } from "@/store/details";
import { toFollowedShows } from "@/push/schedule";
import { DEFAULT_SETTINGS, readSettings, writeSettings } from "@/push/settings";
import type { PushSettings } from "@/push/settings";
import { currentSubscription, subscribe, supportFor, unsubscribe } from "@/push/subscribe";
import type { PushSupport } from "@/push/subscribe";
import {
  deregister,
  putShows,
  readQueued,
  readRegistration,
  register,
  updateDevice,
} from "@/push/worker";
import type { Queued, Registration } from "@/push/worker";

const NONE: Queued = { shows: 0, queued: 0 };

/** Deregisters the device and drops the browser's subscription with it. */
async function teardown(registration: Registration | null): Promise<void> {
  if (registration !== null) await deregister(registration);
  await unsubscribe().catch(ignore);
}

/** Swallows a failure the caller has already accounted for. */
function ignore(): void {}

export type PushState =
  | "loading"
  | "off"
  | "on"
  | "denied"
  | "unsupported"
  | "needs-install"
  | "working";

export interface Push {
  state: PushState;
  settings: PushSettings;
  error: string | null;
  /** Shows being watched, and episodes already queued from them. */
  counts: Queued;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  setQuietHours: (from: number, to: number) => Promise<void>;
  toggleMute: (simklId: number) => Promise<void>;
}

function permissionState(support: PushSupport): PushState | null {
  if (support !== "ok") return support === "unsupported" ? "unsupported" : "needs-install";
  // Denied is terminal on iOS: the prompt never appears a second time.
  return Notification.permission === "denied" ? "denied" : null;
}

export function usePush(
  token: string | null,
  shows: readonly LibraryShow[],
  details: ReadonlyMap<number, ShowDetail>,
): Push {
  const [state, setState] = useState<PushState>("loading");
  const [settings, setSettings] = useState<PushSettings>(DEFAULT_SETTINGS);
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [counts, setCounts] = useState<Queued>(NONE);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const loaded = await load();
      setSettings(loaded.settings);
      setRegistration(loaded.registration);
      setState(loaded.state);
      setCounts(await countsFor(loaded.registration, loaded.state));
    })();
  }, []);

  /**
   * Uploads whatever is currently known, and answers how many were accepted.
   * Safe to call repeatedly; the Worker replaces rather than appends.
   */
  const upload = useCallback(
    (to: Registration, next: PushSettings) =>
      putShows(to, toFollowedShows(shows, details, new Set(next.muted))),
    [shows, details],
  );

  // Keeps the Worker current as the library changes, without the settings
  // screen having to be open. The count deliberately is not recorded here:
  // it belongs to an action the user took, and writing it from a background
  // sync would be a render triggered by a request nobody asked for.
  useEffect(() => {
    if (state !== "on" || registration === null || shows.length === 0) return;
    void upload(registration, settings).catch(ignore);
  }, [state, registration, settings, shows, upload]);

  const enable = useCallback(async () => {
    if (token === null) return;
    setError(null);
    setState("working");
    try {
      const next = await enrol(token, settings);
      if (next === null) {
        setState(Notification.permission === "denied" ? "denied" : "off");
        return;
      }
      setRegistration(next);
      setState("on");
      // The Worker builds the queue on its next tick, so right after enabling
      // the queued count is zero; it catches up when the screen is reopened.
      setCounts(await upload(next, settings));
    } catch (cause) {
      await unsubscribe().catch(ignore);
      setError(cause instanceof Error ? cause.message : "Could not turn notifications on");
      setState("off");
    }
  }, [token, settings, upload]);

  const disable = useCallback(async () => {
    setState("working");
    await teardown(registration);
    setRegistration(null);
    setCounts(NONE);
    setState("off");
  }, [registration]);

  const persist = useCallback(
    async (next: PushSettings) => {
      setSettings(next);
      await writeSettings(next);
      if (registration === null) return;
      // Quiet hours go to the Worker, which is what decides whether a send
      // happens; the mute list changes which episodes are queued at all.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await updateDevice(registration, timezone, { from: next.quietFrom, to: next.quietTo }).catch(
        ignore,
      );
      const result = await upload(registration, next).catch(() => null);
      if (result !== null) setCounts(result);
    },
    [registration, upload],
  );

  return {
    state,
    settings,
    error,
    counts,
    enable,
    disable,
    setQuietHours: (from, to) => persist({ ...settings, quietFrom: from, quietTo: to }),
    toggleMute: (simklId) => persist({ ...settings, muted: withMute(settings.muted, simklId) }),
  };
}

/** What the Worker currently holds, for a device that is switched on. */
function countsFor(registration: Registration | null, state: PushState): Promise<Queued> {
  if (registration === null || state !== "on") return Promise.resolve(NONE);
  return readQueued(registration).catch(() => NONE);
}

/** Everything the hook needs to know before it can show a state. */
async function load(): Promise<{
  settings: PushSettings;
  registration: Registration | null;
  state: PushState;
}> {
  const settings = await readSettings();
  const blocked = permissionState(supportFor());
  if (blocked !== null) return { settings, registration: null, state: blocked };
  const registration = await readRegistration();
  const subscription = await currentSubscription();
  // Both halves have to exist. A subscription without a registration notifies
  // nobody, and a registration whose subscription the browser dropped is a
  // device the Worker will discover is gone the first time it sends.
  const on = registration !== null && subscription !== null;
  return { settings, registration, state: on ? "on" : "off" };
}

/**
 * Permission, subscription, registration. Null means permission was refused,
 * which is a state the screen explains rather than an error it reports.
 */
async function enrol(token: string, settings: PushSettings): Promise<Registration | null> {
  const subscription = await subscribe();
  if (subscription === null) return null;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return register(token, subscription, timezone, {
    from: settings.quietFrom,
    to: settings.quietTo,
  });
}

/** Adds or removes a show from the mute list, whichever it is not already. */
function withMute(muted: readonly number[], simklId: number): number[] {
  return muted.includes(simklId) ? muted.filter((id) => id !== simklId) : [...muted, simklId];
}
